import { useToast } from '@app/core/notifications';
import { pushTo } from '@app/core/routing';
import {
  EnrichedVideoEditorEditListItemDto,
  GetFragmentDto,
  ListEditsVideoEditorEditsQueryResponse,
} from '@app/modules/fragments/fragment.types';
import { useFragmentQuery } from '@app/modules/fragments/hooks/useFragment';
import {
  SOCIAL_EXPORT_FRAGMENT_SOURCE,
  useAvailableSocialExportSources,
} from '@app/modules/fragments/hooks/useAvailableSocialExportSources';
import { routeGenerator } from '@app/modules/fragments/routing/FragmentsRoutes';
import { getFragmentMediaUrl } from '@app/modules/fragments/utils/fragment-media-url';
import { SocialPlatform, socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { useLastUsedPlatformForStation } from '@app/modules/social-export/hooks/useLastUsedPlatformForStation';
import {
  type SocialExportPublishForm,
  useSocialExportUploadConnectionFlow,
} from '@app/modules/social-export/hooks/useSocialExportUploadConnectionFlow';
import { publishMessages } from '@app/modules/social-export/publish';
import { getEditRenderedVideoUrl } from '@app/modules/video-editions';
import {
  SocialExportGetStationConnectionStateDto,
  useFragmentsCreateFragmentSocialPublish,
  useVideoEditorCreateEditSocialPublish,
  webAppGetFragmentSocialUploadsByFragmentIdQueryKey,
  webAppListEditsVideoEditorEditsQueryKey,
} from '@mi-front/api-v2';
import { type UseQueryResult, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';

export type { SocialExportPublishForm };

export interface UseSocialExportUploadPageResult {
  fragmentId: string;
  /** Resolved platform, or undefined while last-used is still loading / unknown id redirected. */
  platform: SocialPlatform | undefined;
  fragmentQuery: ReturnType<typeof useFragmentQuery>;
  /** Signed media URL when assets exist; undefined while media is still generating. */
  fragmentMediaUrl: string | undefined;
  settingsQuery: ReturnType<typeof useSocialExportUploadConnectionFlow>['settingsQuery'];
  editsListQuery: UseQueryResult<ListEditsVideoEditorEditsQueryResponse, unknown> & {
    queryKey: ReturnType<typeof webAppListEditsVideoEditorEditsQueryKey>;
  };
  selectedSource: string;
  setSelectedSource: (value: string) => void;
  edits: EnrichedVideoEditorEditListItemDto[];
  isFragmentSourceAllowed: boolean;
  hasAvailableSources: boolean;
  showMediaSelector: boolean;
  /** True when the selected source is a publishable edit (render pending or completed). */
  hasSelectedPublishableEdit: boolean;
  /** Selected edit when source is a video-editor edit; null for fragment original media. */
  selectedEdit: EnrichedVideoEditorEditListItemDto | null;
  renderedVideoUrl: string | null;
  PublishForm: SocialExportPublishForm | undefined;
  handleUpload: (fragment: GetFragmentDto, data: Record<string, unknown>) => Promise<void>;
  connections: SocialExportGetStationConnectionStateDto[];
  selectedConnectionId: string | null;
  setSelectedConnectionId: (id: string | null) => void;
  areConnectionsResolved: boolean;
  /** True while resolving last-used platform for a platform-less URL. */
  isResolvingPlatform: boolean;
}

/**
 * Owns routing, source selection, connection settings, rendered preview URL, and the
 * create-upload mutation for the fragment social export upload flow.
 */
export function useSocialExportUploadPage(): UseSocialExportUploadPageResult {
  const { fragmentId, platform: platformParam } = useParams<{
    fragmentId: string;
    platform?: string;
  }>();
  const { $t } = useIntl();
  const { notifyInfo, notifyError } = useToast();
  const queryClient = useQueryClient();
  const { search } = useLocation();
  /** Preselected video-editor edit id from the upload URL (`editId` preferred; `projectId` kept for backward compatibility). */
  const searchParams = new URLSearchParams(search);
  const editIdFromUrl = searchParams.get('editId') ?? searchParams.get('projectId') ?? undefined;

  const [selectedSource, setSelectedSource] = useState<string>(
    () => editIdFromUrl ?? SOCIAL_EXPORT_FRAGMENT_SOURCE,
  );

  const fragmentQuery = useFragmentQuery(fragmentId!);
  const fragmentMediaUrl = fragmentQuery.data ? getFragmentMediaUrl(fragmentQuery.data) : undefined;
  const { editsListQuery, edits, publishableEdits, isFragmentSourceAllowed, hasAvailableSources } =
    useAvailableSocialExportSources({
      fragmentId: fragmentId!,
      stationId: fragmentQuery.data?.stationId,
    });

  const { platform: lastUsedPlatform, areConnectionsResolved: areLastUsedResolved } =
    useLastUsedPlatformForStation(fragmentQuery.data?.stationId);

  const platformFromUrl = useMemo(() => {
    if (!platformParam) {
      return undefined;
    }
    try {
      return socialPlatforms.get(platformParam.toUpperCase());
    } catch {
      return null;
    }
  }, [platformParam]);

  // Unknown platform id in the URL → send the user back to the publish tab.
  useEffect(() => {
    if (platformFromUrl === null && fragmentId) {
      pushTo(routeGenerator.publish(fragmentId), true);
    }
  }, [platformFromUrl, fragmentId]);

  const platform = platformFromUrl || lastUsedPlatform;

  // Canonicalize platform-less URLs once last-used (or fallback) is known.
  useEffect(() => {
    if (platformParam || !platform || !fragmentQuery.data) {
      return;
    }
    pushTo(routeGenerator.upload(fragmentQuery.data, platform, editIdFromUrl), true);
  }, [platformParam, platform, fragmentQuery.data, editIdFromUrl]);

  const connectionFlow = useSocialExportUploadConnectionFlow(
    fragmentQuery.data?.stationId,
    // Hook must receive a platform; use a stable placeholder until resolved (form is gated).
    platform ?? socialPlatforms.all()[0]!,
  );

  const selectedEdit =
    selectedSource !== SOCIAL_EXPORT_FRAGMENT_SOURCE
      ? (edits.find((edit) => edit.id === selectedSource) ?? null)
      : null;

  const { url: renderedVideoUrl } = getEditRenderedVideoUrl(selectedEdit, {
    mode: 'playback',
  });

  const showMediaSelector = publishableEdits.length > 0;
  const hasSelectedPublishableEdit = publishableEdits.some((edit) => edit.id === selectedSource);

  useEffect(() => {
    if (editIdFromUrl) {
      setSelectedSource(editIdFromUrl);
    }
  }, [editIdFromUrl]);

  useEffect(() => {
    if (editIdFromUrl || isFragmentSourceAllowed) return;
    if (selectedSource !== SOCIAL_EXPORT_FRAGMENT_SOURCE) return;

    const firstPublishableEditId = publishableEdits[0]?.id;
    if (!firstPublishableEditId) return;

    setSelectedSource(firstPublishableEditId);
  }, [isFragmentSourceAllowed, editIdFromUrl, publishableEdits, selectedSource]);

  const onPublishSuccess = async () => {
    await queryClient.invalidateQueries({
      queryKey: webAppGetFragmentSocialUploadsByFragmentIdQueryKey(fragmentId!),
    });
    notifyInfo($t(publishMessages.publicationStarted));
  };

  const onPublishError = (error: Error) => {
    notifyError($t(publishMessages.startPublicationError));
    console.log(error);
  };

  const fragmentPublishMutation = useFragmentsCreateFragmentSocialPublish({
    mutation: {
      onSuccess: onPublishSuccess,
      onError: onPublishError,
    },
  });

  const editPublishMutation = useVideoEditorCreateEditSocialPublish({
    mutation: {
      onSuccess: onPublishSuccess,
      onError: onPublishError,
    },
  });

  const handleUpload = async (fragment: GetFragmentDto, data: Record<string, unknown>) => {
    try {
      if (!platform) {
        return;
      }

      if (selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE && !isFragmentSourceAllowed) {
        notifyError($t(publishMessages.noAllowedSourceError));
        return;
      }

      if (connectionFlow.isConnectionsLoading) {
        notifyError($t(publishMessages.connectionNotReady));
        return;
      }

      if (!connectionFlow.selectedConnectionId) {
        notifyError(
          $t(publishMessages.noOAuthConnectionForPlatform, {
            platformName: platform.name,
          }),
        );
        return;
      }

      if (selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE) {
        await fragmentPublishMutation.mutateAsync({
          fragmentId: fragment.id,
          data: {
            connectionId: connectionFlow.selectedConnectionId,
            data,
          },
        });
      } else {
        await editPublishMutation.mutateAsync({
          id: selectedSource,
          data: {
            connectionId: connectionFlow.selectedConnectionId,
            data,
          },
        });
      }
      pushTo(`/fragments/${fragment.id}/publish`);
    } catch (error: unknown) {
      notifyError((error as Error).message);
    }
  };

  const isResolvingPlatform = platformFromUrl === undefined && (!areLastUsedResolved || !platform);

  return {
    fragmentId: fragmentId!,
    platform: platformFromUrl === null ? undefined : platform,
    fragmentQuery,
    fragmentMediaUrl,
    settingsQuery: connectionFlow.settingsQuery,
    editsListQuery,
    selectedSource,
    setSelectedSource,
    edits,
    isFragmentSourceAllowed,
    hasAvailableSources,
    showMediaSelector,
    hasSelectedPublishableEdit,
    selectedEdit,
    renderedVideoUrl,
    PublishForm: platform ? connectionFlow.PublishForm : undefined,
    handleUpload,
    connections: connectionFlow.connections,
    selectedConnectionId: connectionFlow.selectedConnectionId,
    setSelectedConnectionId: connectionFlow.setSelectedConnectionId,
    areConnectionsResolved: connectionFlow.areConnectionsResolved,
    isResolvingPlatform,
  };
}
